import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";

export interface AdminSearchEntry {
  title: string;
  href: string;
  icon: IconName;
  group: string;
  description?: string;
  keywords?: string[];
}

interface AdminGlobalSearchProps {
  entries: AdminSearchEntry[];
  lang: string;
  className?: string;
}

const copy = {
  fr: {
    placeholder: "Rechercher une page, une ressource, une action...",
    button: "Recherche globale",
    empty: "Aucun résultat trouvé.",
    shortcut: "Ctrl K",
  },
  en: {
    placeholder: "Search a page, resource or action...",
    button: "Global search",
    empty: "No result found.",
    shortcut: "Ctrl K",
  },
};

export function AdminGlobalSearch({ entries, lang, className }: AdminGlobalSearchProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const content = copy[lang === "en" ? "en" : "fr"];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (!isShortcut) return;
      event.preventDefault();
      setOpen((value) => !value);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const groupedEntries = useMemo(() => {
    return entries.reduce<Record<string, AdminSearchEntry[]>>((acc, entry) => {
      acc[entry.group] = acc[entry.group] ?? [];
      acc[entry.group].push(entry);
      return acc;
    }, {});
  }, [entries]);

  const run = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "group hidden h-10 min-w-[280px] items-center gap-3 rounded-full border border-border/40 bg-card/50 px-3.5 text-left text-sm text-muted-foreground transition-colors hover:border-border/60 hover:bg-muted/30 hover:text-foreground md:flex xl:min-w-[380px]",
          className,
        )}
      >
        <Icon name="search" className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
        <span className="min-w-0 flex-1 truncate">{content.button}</span>
        <kbd className="rounded-full border border-border/40 bg-muted/30 px-2 py-1 text-[0.62rem] font-semibold tracking-[0.14em] text-muted-foreground">
          {content.shortcut}
        </kbd>
      </button>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="grid h-10 w-10 place-items-center rounded-full border border-border/40 bg-card/50 text-muted-foreground transition-colors hover:text-foreground md:hidden"
      >
        <Icon name="search" className="h-4 w-4" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={content.placeholder} />
        <CommandList className="max-h-[420px] bg-popover px-2 py-2">
          <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">{content.empty}</CommandEmpty>
          {Object.entries(groupedEntries).map(([group, groupEntries]) => (
            <CommandGroup key={group} heading={group}>
              {groupEntries.map((entry) => (
                <CommandItem
                  key={`${entry.group}-${entry.href}-${entry.title}`}
                  value={[entry.title, entry.description, ...(entry.keywords ?? [])].filter(Boolean).join(" ")}
                  onSelect={() => run(entry.href)}
                  className="group mb-1 grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 rounded-2xl px-3 py-3 text-muted-foreground data-[selected=true]:bg-muted/30 data-[selected=true]:text-foreground"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full border border-border/40 bg-card/50 text-muted-foreground group-data-[selected=true]:border-primary/25 group-data-[selected=true]:text-primary">
                    <Icon name={entry.icon} className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{entry.title}</span>
                    {entry.description ? <span className="mt-0.5 block truncate text-xs text-muted-foreground">{entry.description}</span> : null}
                  </span>
                  <Icon name="arrowRight" className="h-4 w-4 text-muted-foreground/50 group-data-[selected=true]:text-primary" />
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
